import { SelectionModel } from '@angular/cdk/collections';
import { CdkDrag, CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { clone, cloneDeep } from 'lodash';
import { finalize, first, of, switchMap, tap } from 'rxjs';
import { GlobalSpinnerActions } from 'src/app/shared/ui-kit/global-spinner/actions-types';
import { boardStructureModified, boardStructureReset as boardStructureReset } from '../../board-structure.actions';
import { BoardColumnStructure } from '../../models/board-column-structure.model';
import { BoardStructure } from '../../models/board-structure.model';
import { BoardStructureService } from '../../services/board-structure.service';

@Component({
    selector: 'app-edit-board-structure-dialog',
    templateUrl: './edit-board-structure-dialog.component.html',
    styleUrls: ['./edit-board-structure-dialog.component.scss'],
    standalone: false
})
export class EditBoardStructureDialogComponent implements OnInit {

  editableColumns: BoardColumnStructure[] | undefined
  columnsSelection: SelectionModel<BoardColumnStructure> | undefined
  disableCheckBoxes: boolean = false
  saveBtnDisabled: boolean = true

  private currentBoardStructure: BoardStructure | undefined

  constructor(public dialogRef: MatDialogRef<EditBoardStructureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public boardName: string,
    private readonly appStore: Store,
    private readonly boardStructureService: BoardStructureService) {

    of(this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())).pipe(
      switchMap(() => this.boardStructureService.getBoardStructure$(boardName)),
      tap(structure => {
        this.currentBoardStructure = structure
        this.editableColumns = structure?.columns.filter(c => c.isEditable) ?? []
        this.columnsSelection = new SelectionModel<BoardColumnStructure>(true, this.editableColumns.filter(c => c.isSelected))
        this.columnsSelection.changed.pipe(first()).subscribe(() => this.saveBtnDisabled = false)
      }),
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner()))
    ).subscribe()
  }

  static createConfigurations(boardName: string): MatDialogConfig {
    return {
      width: '570px',
      data: boardName,
      autoFocus: false
    }
  }

  ngOnInit(): void {
  }

  onCancelClick() {
    this.dialogRef.close(null)
  }

  onResetClick() {
    of(this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())).pipe(
      switchMap(() => this.boardStructureService.getDefaultBoardStructure$(this.boardName)),
      switchMap(structure => this.boardStructureService.saveBoardStructure$(structure!)
        .pipe(
          tap(() => this.dialogRef.close()),
          tap(() => this.appStore.dispatch(boardStructureReset(structure)))
        )
      ),
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
    ).subscribe()
  }

  onSaveClick() {

    const columns: BoardColumnStructure[] = cloneDeep(this.editableColumns)!

    columns.forEach(c => c.isSelected = this.columnsSelection!.selected.map(selected => selected.name).includes(c.name))
    this.currentBoardStructure!.columns.forEach(c => { if (!c.isEditable) columns.splice(this.currentBoardStructure!.columns.indexOf(c), 0, c) })

    const structure = {
      name: this.boardName,
      columns: columns,
      version: this.currentBoardStructure!.version
    }

    this.boardStructureService.saveBoardStructure$(structure)
      .pipe(
        tap(() => this.dialogRef.close()),
        tap(() => this.appStore.dispatch(boardStructureModified(structure)))
      )
      .subscribe()
  }

  drop(event: CdkDragDrop<BoardColumnStructure[] | undefined>) {
    if (!event) return
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data!, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data!,
        event.container.data!,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  sortDamPrefixedItem = (index: number, item: CdkDrag<number>) => {
    if (!this.editableColumns) return false
    return !(this.editableColumns[index]?.isFixed)
  }
}
